import { WebAuth } from "auth0-js";
import config from "@/config/env.config.json";
import * as AppStorage from "@/utils/appStorage";
import usersApi from "@/api/users";
import store from "@/store";
import router from "@/router";
import logger from "@/utils/logger";
import phoneHelper from "@/utils/phoneHelper";

const envConfig = config[process.env.NODE_ENV];
const redirectUri = `${envConfig.baseUrl}/callback/auth0/`;
const loginUrl = `${envConfig.baseUrl}/login`;

const auth0Client = {
  webAuth: null,
};

auth0Client.getWebAuth = () => {
  if (!auth0Client.webAuth) {
    logger.info("creating new webauth instance");
    auth0Client.webAuth = new WebAuth({
      domain: envConfig.auth0.domain,
      clientID: envConfig.auth0.clientID,
      audience: envConfig.auth0.audience,
      responseType: "code token id_token",
      redirectUri,
    });
  }

  return auth0Client.webAuth;
};

auth0Client.clearAuthInfoFromStorage = () => {
  AppStorage.remove("loginPhoneNumber");
  AppStorage.remove("accessToken");
  AppStorage.remove("idToken");
  AppStorage.remove("auth0UserSub");
};

auth0Client.storeAuth0ResultProps = (result) => {
  AppStorage.set("accessToken", result.accessToken);
  AppStorage.set("idToken", result.idToken);
  AppStorage.set("auth0UserSub", result.idTokenPayload.sub);
};

auth0Client.sendSmsCode = (phoneNumber, email) => {
  logger.info("store", store);
  return new Promise((resolve, reject) => {
    auth0Client.getWebAuth().passwordlessStart(
      {
        connection: phoneNumber ? "sms" : "email",
        send: "code",
        phoneNumber: phoneNumber
          ? phoneHelper.convertToCountryCode(phoneNumber)
          : undefined,
        email: email || undefined,
      },
      (error, result) => {
        if (error) return reject(error);
        return resolve(result);
      }
    );
  });
};

auth0Client.verifySmsCode = (verificationCode, phoneNumber, email) => {
  return new Promise((resolve, reject) => {
    let options = {
      connection: phoneNumber ? "sms" : "email",
      verificationCode,
    };

    if (phoneNumber) {
      options.phoneNumber = phoneHelper.convertToCountryCode(phoneNumber);
    } else {
      options.email = email;
    }

    auth0Client.getWebAuth().passwordlessLogin(options, (error, result) => {
      if (error) return reject(error);
      return resolve(result);
    });
  });
};

auth0Client.parseHash = () => {
  return new Promise((resolve, reject) => {
    auth0Client.getWebAuth().parseHash((error, result) => {
      if (error) return reject(error);
      logger.info(result);
      if (result && result.accessToken) {
        // Store into local storage
        auth0Client.storeAuth0ResultProps(result);
        usersApi.createHistoricalLogin(result.idTokenPayload.sub);
      }
      return resolve(result);
    });
  });
};

auth0Client.parseHashAndNavigate = async () => {
  try {
    const result = await auth0Client.parseHash();

    if (!result) {
      // Incase some one navigated to the callback url directly for whatever reason,
      // redirect to either login page or dashboard page depending if user is authed
      await auth0Client.checkSession(); // Will go to logout automatically if not a valid session
      router.push({
        name: "Dashboard",
      });
      return;
    }

    // const { idTokenPayload } = result;
    // logger.info("idTokenPayload", idTokenPayload);
    // await usersApi.getUserByAuth0Id(idTokenPayload.sub);
    router.push({
      name: "Dashboard",
    });
  } catch (error) {
    logger.error(error);
    auth0Client.logout({ returnTo: loginUrl });
  }
};

auth0Client.checkSession = () => {
  return new Promise((resolve, reject) => {
    const webAuth = auth0Client.getWebAuth();
    webAuth.checkSession({}, (error, result) => {
      // error = {};
      if (error) {
        auth0Client.clearAuthInfoFromStorage();
        webAuth.logout({ returnTo: loginUrl });
        return reject(error);
      }
      logger.info("new tokens", result);
      // Store into local storage
      auth0Client.storeAuth0ResultProps(result);
      return resolve(result);
    });
  });
};

auth0Client.logout = () => {
  const webAuth = auth0Client.getWebAuth();
  auth0Client.clearAuthInfoFromStorage();
  webAuth.logout({ returnTo: loginUrl });
};

export default auth0Client;
